import { getCalendarStatusMeta, toCalendarStatusValue } from "./calendarStatusMap";

const normalize = (value) => String(value || "").trim().toLowerCase();

export const getUserDisplayName = (user) =>
    user?.fullName
    || `${user?.firstName || user?.prenom || ""} ${user?.lastName || user?.nom || ""}`.trim()
    || user?.username
    || user?.login
    || `User#${user?.id ?? ""}`;

export const formatCalendarDate = (value, withTime = true) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";

    return new Intl.DateTimeFormat("fr-FR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
    }).format(date);
};

export const toDatetimeLocalValue = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";

    const timezoneOffset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - timezoneOffset).toISOString().slice(0, 16);
};

export const toIsoStringOrNull = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toISOString();
};

export const toIntOrNull = (value) => {
    if (value == null || value === "") return null;
    const parsed = Number(String(value).trim());
    return Number.isFinite(parsed) ? Math.trunc(parsed) : null;
};

export const toDecimalOrNull = (value) => {
    if (value == null || value === "") return null;
    const parsed = Number(String(value).trim().replace(",", "."));
    return Number.isNaN(parsed) ? null : String(parsed);
};

export const toFloat = (value) => parseFloat(value) || 0;

export const calculateExpirationDate = (endDate, numberOfDays) => {
    if (!endDate) return null;
    const end = new Date(endDate);
    if (Number.isNaN(end.getTime())) return null;
    return new Date(end.getTime() + (numberOfDays * 24 * 60 * 60 * 1000)).toISOString();
};

export const getLocationLabel = (event) => {
    if (!event) return "—";

    if (event.locationType === "OFFSITE") return event.offsiteLocation || "Lieu externe";
    if (event.locationType === "VIRTUELLE") return event.offsiteLocation || event.salle?.nom || "Lien visio";
    return event.salle?.nom || event.offsiteLocation || "—";
};

export const getEventSearchHaystack = (event, participants = []) =>
    [
        event?.titre,
        event?.description,
        event?.moduleCle?.nom,
        event?.moduleCleNom,
        event?.reference,
        event?.offsiteLocation,
        event?.salle?.nom,
        ...participants.map((participant) => participant?.fullName),
    ]
        .filter(Boolean)
        .map(normalize)
        .join(" ");

export const eventMatchesSearch = (event, searchTerm, participants = []) => {
    if (!searchTerm) return true;
    return getEventSearchHaystack(event, participants).includes(normalize(searchTerm));
};

export const getParticipantUserId = (participant) => {
    const rawId =
        participant?.userId
        ?? participant?.user?.id
        ?? participant?.idUser
        ?? participant?.utilisateurId
        ?? participant?.collaborateurId
        ?? participant?.learnerId
        ?? null;

    return rawId == null ? null : Number(rawId);
};

export const getRegistrationDeadlineAt = (event) => {
    if (event?.registrationDeadlineAt) return event.registrationDeadlineAt;
    if (!event?.dateDebut || event?.registrationDeadlineHours == null) return null;

    const startDate = new Date(event.dateDebut);
    if (Number.isNaN(startDate.getTime())) return null;

    return new Date(startDate.getTime() - (Number(event.registrationDeadlineHours) * 60 * 60 * 1000)).toISOString();
};

export const getApprovedRegistrationCount = (event) => {
    const count = Number(event?.approvedRegistrationsCount);
    return Number.isFinite(count) && count >= 0 ? count : 0;
};

export const getRemainingPlaces = (event) => {
    if (event?.remainingPlaces != null) {
        const remaining = Number(event.remainingPlaces);
        return Number.isFinite(remaining) ? remaining : null;
    }

    if (event?.maxPlaces == null) return null;
    return Math.max(Number(event.maxPlaces) - getApprovedRegistrationCount(event), 0);
};

export const getEventRegistrationAvailability = (event) => {
    const statusValue = toCalendarStatusValue(event?.status);
    const deadlineAt = getRegistrationDeadlineAt(event);
    const approvedCount = getApprovedRegistrationCount(event);
    const remainingPlaces = getRemainingPlaces(event);
    const isPlanned = statusValue === "PLANIFIE";
    const allowRegistration = Boolean(event?.allowRegistration);
    const now = new Date();
    const deadlinePassed = deadlineAt ? new Date(deadlineAt) < now : false;
    const noPlaceLeft = event?.maxPlaces != null && approvedCount >= Number(event.maxPlaces);

    if (!isPlanned) {
        return {
            allowed: false,
            reason: "Inscription disponible uniquement pour les sessions planifiees.",
            approvedCount,
            remainingPlaces,
            deadlineAt,
        };
    }

    if (!allowRegistration) {
        return {
            allowed: false,
            reason: "Les inscriptions ne sont pas ouvertes pour cette session.",
            approvedCount,
            remainingPlaces,
            deadlineAt,
        };
    }

    if (deadlinePassed) {
        return {
            allowed: false,
            reason: "La date limite d'inscription est depassee.",
            approvedCount,
            remainingPlaces,
            deadlineAt,
        };
    }

    if (noPlaceLeft) {
        return {
            allowed: false,
            reason: "Le nombre maximal de places a ete atteint.",
            approvedCount,
            remainingPlaces,
            deadlineAt,
        };
    }

    return {
        allowed: true,
        reason: "",
        approvedCount,
        remainingPlaces,
        deadlineAt,
    };
};

export const buildVisibleEvents = ({
    events,
    selectedStatuses,
    searchTerm,
    roleConfig,
    currentUser,
    directReports,
    assignmentsMap,
    selectedCollaboratorId,
    activeView,
}) => {
    const currentUserId = currentUser?.id;
    const teamUserIds = new Set((directReports || []).map((user) => user?.id).filter(Boolean));
    const statusSet = new Set((selectedStatuses || []).map((status) => toCalendarStatusValue(status)));

    return (events || []).filter((event) => {
        const participants = assignmentsMap[event.id] || [];
        const participantIds = new Set(
            participants
                .map((participant) => getParticipantUserId(participant))
                .filter((userId) => userId != null)
        );
        const isAssignedToCurrentUser = currentUserId ? participantIds.has(currentUserId) : false;
        const isAssignedToTeam = [...teamUserIds].some((userId) => participantIds.has(userId));
        const eventStatus = toCalendarStatusValue(event.status);
        const isPlannedEvent = eventStatus === "PLANIFIE";

        let roleMatch = true;
        if (roleConfig.isManager && activeView === "TEAM_EVENTS") {
            roleMatch = isAssignedToTeam;
            if (roleMatch && selectedCollaboratorId) {
                roleMatch = participantIds.has(Number(selectedCollaboratorId));
            }
        } else if (roleConfig.isManager && activeView === "PLANNED_EVENTS") {
            roleMatch = isPlannedEvent;
        } else if (roleConfig.isCollaborator && activeView === "MY_EVENTS") {
            roleMatch = isAssignedToCurrentUser;
        } else if (roleConfig.isCollaborator && activeView === "PLANNED_EVENTS") {
            roleMatch = isPlannedEvent;
        }

        const statusMatch = !statusSet.size || statusSet.has(eventStatus);
        const searchMatch = eventMatchesSearch(event, searchTerm, participants);

        return roleMatch && statusMatch && searchMatch;
    });
};

export const mapEventToCalendarEntry = (event, assignmentsMap) => {
    const statusMeta = getCalendarStatusMeta(event?.status);
    const participants = assignmentsMap?.[event?.id] || [];

    return {
        id: String(event.id),
        title: event.titre || "Evenement",
        start: event.dateDebut,
        end: event.dateFin,
        allDay: false,
        backgroundColor: statusMeta.background,
        borderColor: statusMeta.borderColor,
        textColor: statusMeta.color,
        extendedProps: {
            rawEvent: event,
            participants,
            statusMeta,
            participantCount: participants.length,
            locationLabel: getLocationLabel(event),
        },
    };
};

export const buildCreatePayload = (form) => ({
    typeLieu: form.locationType === "VIRTUELLE" ? "VIRTUEL" : form.locationType,
    titre: form.titre.trim(),
    description: form.description?.trim() || null,
    moduleCleId: Number(form.moduleCleId),
    locationType: form.locationType,
    salleId: form.locationType === "OFFSITE" ? null : (form.salleId ? Number(form.salleId) : null),
    sallePhysiqueId: form.locationType === "ONSITE" && form.salleId ? Number(form.salleId) : null,
    salleVirtuelleId: form.locationType === "VIRTUELLE" && form.salleId ? Number(form.salleId) : null,
    offsiteLocation: form.locationType === "OFFSITE" ? form.offsiteLocation?.trim() || null : null,
    lieuExact: form.locationType === "OFFSITE" ? form.offsiteLocation?.trim() || null : null,
    dateDebut: toIsoStringOrNull(form.dateDebut),
    dateFin: toIsoStringOrNull(form.dateFin),
    allowRegistration: Boolean(form.allowRegistration),
    registrationDeadlineHours: form.allowRegistration ? toIntOrNull(form.registrationDeadlineHours) : null,
    isPeriodic: Boolean(form.isPeriodic),
    maxPlaces: toIntOrNull(form.maxPlaces),
    currency: form.currency?.trim() || "MAD",
});

export const buildUpdatePayload = (event, form) => ({
    ...buildCreatePayload(form),
    reference: event?.reference || null,
});

export const buildRichEventPayload = ({
    event,
    form,
    budget,
    consultants,
    evaluations,
    sessions,
    numberOfDaysHot,
    numberOfDaysCold,
    budgetRestant,
}) => {
    const totalCost = toFloat(budget.tarifFormation)
        + toFloat(budget.repas)
        + toFloat(budget.transport)
        + toFloat(budget.hebergement)
        + toFloat(budget.salleConference)
        + toFloat(budget.autres);

    return {
        ...(event ? buildUpdatePayload(event, form) : buildCreatePayload(form)),
        tarifFormation: toDecimalOrNull(budget.tarifFormation),
        repas: toDecimalOrNull(budget.repas),
        transport: toDecimalOrNull(budget.transport),
        hebergement: toDecimalOrNull(budget.hebergement),
        salleConference: toDecimalOrNull(budget.salleConference),
        autres: toDecimalOrNull(budget.autres),
        autreDescription: budget.autreDescription?.trim() || null,
        cout: toDecimalOrNull(totalCost),
        budgetRestant: toDecimalOrNull(budgetRestant),
        consultantIds: consultants,
        evaluationIds: evaluations,
        numberOfDaysHot,
        numberOfDaysCold,
        dateDebutEvHot: calculateExpirationDate(form.dateFin, numberOfDaysHot),
        dateDebutEvCold: calculateExpirationDate(form.dateFin, numberOfDaysCold),
        sessions: (sessions || []).map((session) => ({
            id: session.id || null,
            sorder: session.sorder,
            name: session.name,
            dureeHours: session.dureeHours,
            startat: toIsoStringOrNull(session.startat),
            endat: toIsoStringOrNull(session.endat),
        })),
    };
};

export const buildReschedulePayload = (eventId, form) => ({
    id: Number(eventId),
    dateDebut: toIsoStringOrNull(form.dateDebut),
    dateFin: toIsoStringOrNull(form.dateFin),
});
